import type { ActivityRecord, GoogleType, TrackPoint, VisitRecord } from './types.js'

const SG_OFFSET_MS = 8 * 3600000

function toSGIso(ts: string): string {
  const d = new Date(ts.replace(' ', 'T') + 'Z')
  return new Date(d.getTime() + SG_OFFSET_MS).toISOString()
}

export function toSGDate(ts: string): string {
  return toSGIso(ts).slice(0, 10)
}

/** Returns MM/DD in SGT */
export function toSGMMDD(ts: string): string {
  const iso = toSGIso(ts)
  return iso.slice(5, 7) + '/' + iso.slice(8, 10)
}

/** Returns YYYY-MM-DDTHH:MM in SGT (for datetime-local input) */
export function toSGDateTimeLocal(ts: string): string {
  return toSGIso(ts).slice(0, 16)
}

export function toSGTimeFull(ts: string): string {
  return toSGIso(ts).slice(11, 19)
}

/** Returns MM-DD HH:MM:SS in SGT */
export function toSGDateTimeFull(ts: string): string {
  const iso = toSGIso(ts)
  return iso.slice(5, 7) + '-' + iso.slice(8, 10) + ' ' + iso.slice(11, 19)
}

export function toEpoch(ts: string): number {
  return new Date(ts.replace(' ', 'T') + 'Z').getTime()
}

export function formatDuration(sec: number): string {
  if (sec < 60) return sec.toFixed(0) + 's'
  if (sec < 3600) return (sec / 60).toFixed(1) + 'min'
  return (sec / 3600).toFixed(1) + 'h'
}

export function accuracyColor(acc: number | null): string {
  if (acc == null) return '#999'
  if (acc < 10) return '#34C759'
  if (acc < 30) return '#007AFF'
  if (acc < 65) return '#FF9500'
  return '#FF3B30'
}

export function speedColor(speed: number | null): string {
  if (speed == null || speed < 0) return '#999'
  const kmh = speed * 3.6
  if (kmh < 2) return '#8E8E93'
  if (kmh < 7) return '#34C759'
  if (kmh < 20) return '#007AFF'
  if (kmh < 60) return '#FF9500'
  return '#FF3B30'
}

export function gapColor(gapSec: number): string {
  if (gapSec < 120) return '#007AFF'
  if (gapSec < 600) return '#FF9500'
  return '#FF3B30'
}

export function computeDistance(g: GoogleType, lat1: number, lng1: number, lat2: number, lng2: number): number {
  return g.maps.geometry.spherical.computeDistanceBetween(new g.maps.LatLng(lat1, lng1), new g.maps.LatLng(lat2, lng2))
}

/** First index where point.epochMs >= target (points must be sorted by _epoch). */
export function lowerBound(points: TrackPoint[], target: number): number {
  let lo = 0
  let hi = points.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (points[mid]!.epochMs < target) lo = mid + 1
    else hi = mid
  }
  return lo
}

/** First index where point.epochMs > target (points must be sorted by _epoch). */
export function upperBound(points: TrackPoint[], target: number): number {
  let lo = 0
  let hi = points.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (points[mid]!.epochMs <= target) lo = mid + 1
    else hi = mid
  }
  return lo
}

// ── 一生足迹 CSV parser ──

export function parseCsvToTrackPoints(csvText: string): TrackPoint[] {
  const lines = csvText.trim().split('\n')
  if (lines.length < 2) return []

  return lines.slice(1).map((line, index) => {
    const cols = line.split(',')
    const ts = Number(cols[0])
    const d = new Date(ts * 1000)
    const timestamp = d.toISOString().slice(0, 19).replace('T', ' ')
    const speed = Number(cols[6])

    return {
      id: index + 1,
      latitude: Number(cols[3]),
      longitude: Number(cols[2]),
      altitude: Number(cols[10]) || null,
      horizontalAccuracy: Number(cols[5]) || null,
      speed: speed < 0 ? null : speed,
      course: Number(cols[4]) || null,
      timestamp,
      source: Number(cols[1]),
      segmentIndex: 0,
    } as TrackPoint
  })
}

// ── sql.js (bundled via npm, WASM from public/) ──

type SqlJsStatic = import('sql.js').SqlJsStatic

let sqlPromise: Promise<SqlJsStatic> | null = null

export function initSql(): Promise<SqlJsStatic> {
  if (!sqlPromise) {
    sqlPromise = import('sql.js').then((mod) => mod.default({ locateFile: () => '/sql-wasm.wasm' }))
  }
  return sqlPromise
}

function tableExists(db: import('sql.js').Database, tableName: string): boolean {
  const escaped = tableName.replaceAll("'", "''")
  const result = db.exec(`SELECT name FROM sqlite_master WHERE type='table' AND name='${escaped}'`)
  return result.length > 0
}

/**
 * Extract VisitRecords from an open sql.js database. Returns an empty array if
 * the table doesn't exist (older DB schema).
 */
export function extractVisitRecords(db: import('sql.js').Database): VisitRecord[] {
  if (!tableExists(db, 'VisitRecords')) return []

  const results = db.exec(`
    SELECT id, arrivalDate, departureDate, latitude, longitude, horizontalAccuracy, invalid
    FROM VisitRecords
    WHERE invalid = 0
    ORDER BY arrivalDate ASC
  `)
  if (results.length === 0) return []
  const cols = results[0]?.columns ?? []
  const rows = results[0]?.values ?? []
  return rows.map((row) => {
    const obj: Record<string, unknown> = {}
    cols.forEach((c, idx) => (obj[c] = row[idx]))
    return obj as unknown as VisitRecord
  })
}

/**
 * Extract Activity rows from an open sql.js database. Returns an empty array if
 * the table doesn't exist (CSV and older debug DBs do not include activities).
 */
export function extractActivityRecords(db: import('sql.js').Database): ActivityRecord[] {
  if (!tableExists(db, 'Activity')) return []

  const hasActivityType = tableExists(db, 'ActivityType')
  const hasLocation = tableExists(db, 'Location')
  const activityTypeColumns = hasActivityType
    ? `
      t.name AS typeName,
      t.color AS typeColor
    `
    : `
      NULL AS typeName,
      NULL AS typeColor
    `
  const locationColumns = hasLocation
    ? `
      COALESCE(NULLIF(l.alias, ''), NULLIF(l.name, '')) AS locationName,
      l.centerLatitude AS locationLatitude,
      l.centerLongitude AS locationLongitude
    `
    : `
      NULL AS locationName,
      NULL AS locationLatitude,
      NULL AS locationLongitude
    `

  const results = db.exec(`
    SELECT
      a.id,
      a.activityTypeId,
      a.source,
      a.category,
      a.startDate,
      a.endDate,
      a.locationId,
      a.notes,
      a.parentActivityId,
      ${activityTypeColumns},
      ${locationColumns}
    FROM Activity a
    ${hasActivityType ? 'LEFT JOIN ActivityType t ON t.id = a.activityTypeId' : ''}
    ${hasLocation ? 'LEFT JOIN Location l ON l.id = a.locationId' : ''}
    ORDER BY a.startDate ASC
  `)
  if (results.length === 0) return []
  const cols = results[0]?.columns ?? []
  const rows = results[0]?.values ?? []
  return rows.map((row) => {
    const obj: Record<string, unknown> = {}
    cols.forEach((c, idx) => (obj[c] = row[idx]))
    const startDate = String(obj.startDate ?? '')
    const endDate = obj.endDate == null ? null : String(obj.endDate)
    return {
      id: Number(obj.id),
      activityTypeId: Number(obj.activityTypeId),
      source: Number(obj.source),
      category: Number(obj.category),
      startDate,
      endDate,
      locationId: obj.locationId == null ? null : Number(obj.locationId),
      notes: obj.notes == null ? null : String(obj.notes),
      parentActivityId: obj.parentActivityId == null ? null : Number(obj.parentActivityId),
      typeName: obj.typeName == null ? null : String(obj.typeName),
      typeColor: obj.typeColor == null ? null : String(obj.typeColor),
      locationName: obj.locationName == null ? null : String(obj.locationName),
      locationLatitude: obj.locationLatitude == null ? null : Number(obj.locationLatitude),
      locationLongitude: obj.locationLongitude == null ? null : Number(obj.locationLongitude),
      startEpochMs: toEpoch(startDate),
      endEpochMs: endDate == null ? null : toEpoch(endDate),
    }
  })
}

/**
 * Deduplicate visits that repeat the same (arrival, departure, lat, lng).
 * Lat/lng are rounded to 5 decimals (~1m) so near-identical records collapse.
 */
export function dedupeVisits(visits: VisitRecord[]): VisitRecord[] {
  const seen = new Set<string>()
  const out: VisitRecord[] = []
  for (const v of visits) {
    const key = [v.arrivalDate, v.departureDate ?? 'null', v.latitude.toFixed(5), v.longitude.toFixed(5)].join('|')
    if (seen.has(key)) continue
    seen.add(key)
    out.push(v)
  }
  return out
}
